import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardList, Clock, CheckCircle2, AlertTriangle, Users, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import FilterBar, { useFilters } from "@/components/FilterBar";
import GanttChart from "@/components/GanttChart";

const statusLabels = {
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  concluido: "Concluido",
  cancelado: "Cancelado",
};

const statusColors = {
  pendente: "bg-slate-100 text-slate-700",
  em_andamento: "bg-blue-100 text-blue-800",
  concluido: "bg-green-100 text-green-800",
  cancelado: "bg-gray-100 text-gray-500",
};

const priorityLabels = { baixa: "Baixa", media: "Media", alta: "Alta", urgente: "Urgente" };
const priorityColors = {
  baixa: "bg-green-100 text-green-800",
  media: "bg-yellow-100 text-yellow-800",
  alta: "bg-orange-100 text-orange-800",
  urgente: "bg-red-100 text-red-800",
};

const chartColors = ["hsl(222, 80%, 40%)", "hsl(45, 93%, 47%)", "hsl(160, 60%, 40%)", "hsl(0, 84%, 60%)"];

export default function DashboardPage() {
  const [allOrders, setAllOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const filters = useFilters();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, ordersRes, clientsRes] = await Promise.all([
          axios.get(`${API}/dashboard`, { withCredentials: true }),
          axios.get(`${API}/orders`, { withCredentials: true }),
          axios.get(`${API}/clients`, { withCredentials: true }),
        ]);
        setLowStock(dashRes.data.low_stock_materials || []);
        setTotalClients(dashRes.data.total_clients);
        setTotalMaterials(dashRes.data.total_materials);
        setAllOrders(ordersRes.data);
        setClients(clientsRes.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = filters.filterOrders(allOrders);

  const ordersPendente = filtered.filter((o) => o.status === "pendente").length;
  const ordersEmAndamento = filtered.filter((o) => o.status === "em_andamento").length;
  const ordersConcluido = filtered.filter((o) => o.status === "concluido").length;
  const ordersCancelado = filtered.filter((o) => o.status === "cancelado").length;

  const now = new Date().toISOString();
  const ordersAtrasado = filtered.filter((o) =>
    o.deadline && o.deadline < now && !["concluido", "cancelado"].includes(o.status)
  ).length;

  if (loading) return <div className="text-sm text-muted-foreground">Carregando dashboard...</div>;

  const chartData = [
    { name: "Pendente", value: ordersPendente },
    { name: "Em Andamento", value: ordersEmAndamento },
    { name: "Concluido", value: ordersConcluido },
    { name: "Cancelado", value: ordersCancelado },
  ];

  const kpis = [
    { label: "Total de Ordens", value: filtered.length, icon: ClipboardList, color: "text-primary" },
    { label: "Em Andamento", value: ordersEmAndamento, icon: Clock, color: "text-blue-600" },
    { label: "Concluidas", value: ordersConcluido, icon: CheckCircle2, color: "text-green-600" },
    { label: "Atrasadas", value: ordersAtrasado, icon: AlertTriangle, color: "text-red-600" },
    { label: "Clientes", value: totalClients, icon: Users, color: "text-primary" },
    { label: "Materiais", value: totalMaterials, icon: Package, color: "text-primary" },
  ];

  const recentOrders = filtered.slice(0, 5);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Visao Geral</h1>
          <p className="text-sm text-muted-foreground mt-1">Acompanhamento da producao em tempo real</p>
        </div>
      </div>

      <FilterBar filters={filters} clients={clients} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border border-border shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
              </div>
              <p className="font-heading text-3xl font-black tracking-tighter">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <Card className="border border-border shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Ordens por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {chartData.map((_, index) => (
                      <Cell key={index} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock Alert */}
        <Card className="border border-border shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Estoque Baixo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lowStock.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Todos os materiais com estoque adequado</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {lowStock.map((m) => (
                  <div key={m.id} className="flex items-center justify-between p-2 rounded bg-yellow-50 border border-yellow-200">
                    <span className="text-sm font-medium">{m.name}</span>
                    <span className="text-sm text-yellow-800 font-semibold">
                      {m.quantity} / {m.min_stock} {m.unit}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delivery Alerts card removed per user request (2026-02) - contracts already 100% delivered were cluttering the dashboard */}

      {/* Gantt Chart */}
      <Card className="border border-border shadow-none" data-testid="gantt-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Cronograma de Producao (Gantt)</CardTitle>
        </CardHeader>
        <CardContent>
          <GanttChart orders={filtered} />
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">
            {filters.hasActiveFilters ? "Ordens Filtradas" : "Ordens Recentes"}
            {filters.hasActiveFilters && <Badge variant="secondary" className="ml-2 text-xs">{filtered.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titulo</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead>Progresso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Nenhuma ordem encontrada
                  </TableCell>
                </TableRow>
              ) : (
                recentOrders.map((order) => {
                  const total = order.stages?.length || 0;
                  const done = order.stages?.filter((s) => s.status === "concluido").length || 0;
                  return (
                    <TableRow key={order.id} data-testid={`recent-order-${order.id}`}>
                      <TableCell className="font-medium">{order.title}</TableCell>
                      <TableCell>{order.client_name}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[order.status]}>
                          {statusLabels[order.status] || order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityColors[order.priority]}>
                          {priorityLabels[order.priority] || order.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: total ? `${(done / total) * 100}%` : "0%" }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{total ? `${done}/${total}` : "-"}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
